import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Globe, AlertTriangle, Loader2 } from "lucide-react";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

/**
 * Wraps a legal page. If the URL has ?lang=<non-en>, replaces the rich JSX
 * body with the machine-translated plain-text fetched from the backend,
 * with a "pending human review" banner.
 *
 * Usage:
 *   <LegalLanguageSwitcher doc="terms">{englishJsxBody}</LegalLanguageSwitcher>
 */
export function LegalLanguageSwitcher({ doc, children }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const lang = (searchParams.get("lang") || "en").toLowerCase();
    const [languages, setLanguages] = useState([{ code: "en", label: "English" }]);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.get("/legal/languages").then(({ data }) => setLanguages(data.languages || []));
    }, []);

    useEffect(() => {
        if (lang === "en") { setResult(null); return; }
        setLoading(true);
        api.get(`/legal/${doc}`, { params: { lang } })
            .then(({ data }) => setResult(data))
            .catch(() => setResult({ source: "error", text: "", flagged: true }))
            .finally(() => setLoading(false));
    }, [doc, lang]);

    const setLang = (code) => {
        const next = new URLSearchParams(searchParams);
        if (code === "en") next.delete("lang"); else next.set("lang", code);
        setSearchParams(next, { replace: true });
    };

    return (
        <div className="mx-auto max-w-6xl px-6 pt-6" data-testid={`legal-lang-${doc}`}>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card p-3">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                    <Globe className="h-3.5 w-3.5" />
                    Language
                </div>
                <div className="w-56">
                    <Select value={lang} onValueChange={setLang}>
                        <SelectTrigger data-testid={`lang-switcher-${doc}`}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {languages.map((l) => (
                                <SelectItem key={l.code} value={l.code} data-testid={`lang-opt-${l.code}`}>
                                    {l.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {lang === "en" ? (
                children
            ) : loading ? (
                <div className="flex items-center justify-center gap-2 py-24 text-sm text-muted-foreground" data-testid="legal-lang-loading">
                    <Loader2 className="h-4 w-4 animate-spin" /> Translating…
                </div>
            ) : result ? (
                <div className="mx-auto max-w-3xl pb-16">
                    {result.flagged && (
                        <div className="mb-6 flex items-start gap-3 rounded-md border-2 border-[hsl(45,93%,47%)]/40 bg-[hsl(45,93%,47%)]/10 p-4 text-sm" data-testid="legal-machine-banner">
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(45,93%,40%)]" />
                            <div>
                                <div className="font-semibold text-foreground">Auto-translated — pending human review</div>
                                <div className="mt-1 text-xs text-muted-foreground">
                                    This translation was generated by machine translation as a courtesy.
                                    The <button
                                        type="button"
                                        onClick={() => setLang("en")}
                                        className="font-semibold text-primary hover:underline"
                                    >English original</button> is the legally binding version until a certified translation is published.
                                </div>
                            </div>
                        </div>
                    )}
                    <pre className="whitespace-pre-wrap break-words rounded-lg border border-border bg-card p-6 font-sans text-sm leading-relaxed text-foreground" data-testid="legal-translated-body">
                        {result.text}
                    </pre>
                </div>
            ) : null}
        </div>
    );
}
